import { SITE_NAME, SITE_URL } from "@/helper/constant";
import { removeTags, t, localePrefix } from "@/helper/helper";
import { baseURL } from "@/services/api";
import { ILocations, ILocationsGeneric } from "global-types/global.types";
import { GetServerSideProps } from "next";
import { NextSeo } from "next-seo";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import styled from "styled-components";
import Head from "next/head";

interface Props {
  data: ILocations<ILocationsGeneric>;
  locale: string;
  slug: string;
}

export default function LocationsDetail({ data, locale }: Props) {
  const breadCrumbs = useRef<any>([]);

  useEffect(() => {
    breadCrumbs.current = [];
  });

  const { asPath } = useRouter();

  // Create breadcrumb schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: t("homePage", locale),
        item: `${SITE_URL}${localePrefix(locale)}`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: t("locations", locale),
        item: `${SITE_URL}${localePrefix(locale)}/locations`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: data.data.name,
        item: `${SITE_URL}${localePrefix(locale)}/locations/${data.data.slug}`,
      },
    ],
  };

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />
      </Head>
      <NextSeo
        title={data?.data?.name + " | " + SITE_NAME}
        // @ts-ignore
        description={removeTags(data?.data?.description).substring(0, 160)}
        additionalMetaTags={[
          {
            name: "title",
            content: `${data?.data?.name} | ${SITE_NAME}`,
          },
        ]}
        canonical={SITE_URL + localePrefix(locale) + asPath}
        openGraph={{
          url: SITE_URL + localePrefix(locale) + asPath,
          title: data?.data?.name + " | " + SITE_NAME,
          // @ts-ignore
          description: removeTags(data?.data?.description).substring(0, 300),
          siteName: SITE_URL,
        }}
      />
      <Wrapper>
        <div className="breadcrumb">
          <Link href={`${localePrefix(locale) || "/"}`}>{t("homePage", locale)}</Link>
          <span className="breadcrumb-seperator" />
          <Link href={`${localePrefix(locale)}/locations`}>{t("locations", locale)}</Link>
          <Link href={`${localePrefix(locale)}/locations/${data.data.slug}`}>
            {data.data.name}
          </Link>
        </div>
        <LocationsTitle>{data.data.name}</LocationsTitle>
        <Des
          dangerouslySetInnerHTML={{
            __html: data.data.description,
          }}
        />
      </Wrapper>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { locale } = ctx;
  const { slug } = ctx.params!;

  const locationsDetail = await fetch(baseURL + `/location/${slug}`, {
    headers: {
      "x-localization": locale ? locale : "az",
    },
  });

  return {
    props: {
      data: await locationsDetail.json(),
      locale,
      slug,
    },
  };
};

const Wrapper = styled.div`
  width: 1287px;
  margin: 0 auto;

  iframe {
    width: 100%;
  }
`;

const LocationsTitle = styled.h2`
  font-style: normal;
  font-weight: 700;
  font-size: 36px;
  line-height: 150%;
  color: #040941;
  margin-bottom: 24px;
`;

const Des = styled.div`
  * {
    font-style: normal;
    font-weight: 500;
    font-size: 20px;
    line-height: 30px;
    color: #989eaf;
  }
`;
